import { CreatePurchaseRequest } from 'stash/types/models/Transaction.js';
import { fetchRequestWithTypes } from './index.js';

// Type definitions
export type Deposit = {
	amount: number;
	contribution_year?: string;
};

export type Buy = {
	card_id: number;
	amount: number;
};

export type BuyWithDepositBody = {
	deposit: Deposit;
	buy?: Buy;
	funding_source_id?: number;
};

export type BasketPurchase = {
	card_id: string | number;
	amount: string;
};

export type BasketBuyWithDepositBody = {
	purchases: BasketPurchase[];
	deposit?: {
		amount: string;
		contribution_year?: string;
	};
	funding_source_id?: string | number;
	initial?: boolean;
};

export type SellTransaction = {
	card_id: number;
	sell_all?: '1';
	value?: number;
};

export const POST_SELL_REQUEST = 'POST_SELL_REQUEST';
export const POST_SELL_SUCCESS = 'POST_SELL_SUCCESS';
export const POST_SELL_ERRORED = 'POST_SELL_ERRORED';

export const postSell = (
	accountId: string,
	card_id: number,
	amount: number | null,
	sellAll: boolean
) => {
	const transaction: SellTransaction = {
		card_id,
	};

	if (sellAll) {
		transaction.sell_all = '1';
	} else if (amount !== null) {
		transaction.value = amount;
	}

	return fetchRequestWithTypes({
		types: [POST_SELL_REQUEST, POST_SELL_SUCCESS, POST_SELL_ERRORED],
		meta: {
			card_id,
			accountId,
		},
		request: {
			method: 'POST',
			path: `/apis/api/v1/users/:uuid/accounts/${accountId}/transactions/sell`,
			body: { transaction },
		},
	});
};

export const postBuyV2 = (body: CreatePurchaseRequest) => {
	return fetchRequestWithTypes({
		types: [POST_BUY_REQUEST, POST_BUY_SUCCESS, POST_BUY_ERRORED],
		request: {
			method: 'POST',
			path: `/apis/orders/v1/transactions/buy`,
			body,
		},
	});
};

// Dispatched by postBuyV2; success is consumed downstream of the buy flow.
export const POST_BUY_REQUEST = 'POST_BUY_REQUEST';
export const POST_BUY_SUCCESS = 'POST_BUY_SUCCESS';
export const POST_BUY_ERRORED = 'POST_BUY_ERRORED';

export const POST_BUY_WITH_DEPOSIT_REQUEST = 'POST_BUY_WITH_DEPOSIT_REQUEST';
export const POST_BUY_WITH_DEPOSIT_SUCCESS = 'POST_BUY_WITH_DEPOSIT_SUCCESS';
export const POST_BUY_WITH_DEPOSIT_ERRORED = 'POST_BUY_WITH_DEPOSIT_ERRORED';

export const postBuyWithDeposit = (
	accountId: string,
	card_id: number | null,
	amount: number,
	contribution_year?: string | null,
	funding_source_id?: number | null
) => {
	const body: BuyWithDepositBody = {
		deposit: {
			amount,
			...(contribution_year ? { contribution_year } : {}),
		},
	};

	const buyingInvestment = card_id && card_id > -1;
	if (buyingInvestment) {
		body.buy = {
			card_id,
			amount,
		};
	}

	if (funding_source_id) body.funding_source_id = funding_source_id;

	return fetchRequestWithTypes({
		types: [
			POST_BUY_WITH_DEPOSIT_REQUEST,
			POST_BUY_WITH_DEPOSIT_SUCCESS,
			POST_BUY_WITH_DEPOSIT_ERRORED,
		],
		meta: {
			card_id,
			accountId,
		},
		passIdempotencyKey: true,
		request: {
			method: 'POST',
			path: `/apis/api/v1/users/:uuid/accounts/${accountId}/transactions/buy_with_deposit`,
			body,
		},
	});
};

// Issues one monolith buy_with_deposit request for a multi-position basket.
export const postBasketBuyWithDeposit = (
	accountId: string,
	body: BasketBuyWithDepositBody
) =>
	fetchRequestWithTypes({
		types: [
			POST_BUY_WITH_DEPOSIT_REQUEST,
			POST_BUY_WITH_DEPOSIT_SUCCESS,
			POST_BUY_WITH_DEPOSIT_ERRORED,
		],
		meta: { accountId },
		passIdempotencyKey: true,
		request: {
			method: 'POST',
			path: `/apis/api/v1/users/:uuid/accounts/${accountId}/transactions/buy_with_deposit`,
			body,
		},
	});
